import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Trash2, AlertCircle, TrendingUp, CheckCircle, MessageSquare, Copy, Check, Database, Plus, History, ChevronLeft, Maximize2, Minimize2, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn, stripMarkdown } from "@/lib/utils";
import { BodyText } from "@/components/ui/typography";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AIChatExportMenu } from "./AIChatExportMenu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { logger } from "@/utils/logger";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: Date;
  followUpSuggestions?: string[];
}

const QUICK_QUESTIONS = [
  { text: "Urgent issues to address", icon: AlertCircle, color: "text-destructive" },
  { text: "Team sentiment this week", icon: TrendingUp, color: "text-primary" },
  { text: "Actions to prioritize", icon: CheckCircle, color: "text-success" },
  { text: "Top concerns from feedback", icon: MessageSquare, color: "text-warning" },
];

interface AIChatPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuestion?: string;
}

interface Conversation {
  conversation_id: string;
  first_message: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
}

export const AIChatPanel = ({ open, onOpenChange, initialQuestion }: AIChatPanelProps) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [processedInitialQuestion, setProcessedInitialQuestion] = useState<string | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [showRecentChats, setShowRecentChats] = useState(false);
  const [recentConversations, setRecentConversations] = useState<Conversation[]>([]);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Fetch data context (submissions and pages count)
  const { data: dataContext } = useQuery({
    queryKey: ["ai-data-context", user?.id],
    queryFn: async () => {
      if (!user?.id) return { submissions: 0, pages: 0 };
      
      const { data: pages } = await supabase
        .from("feedback_pages")
        .select("id")
        .eq("user_id", user.id);
      
      const pageIds = pages?.map(p => p.id) || [];
      
      if (pageIds.length === 0) return { submissions: 0, pages: 0 };
      
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { count } = await supabase
        .from("feedback_submissions")
        .select("id", { count: "exact", head: true })
        .in("page_id", pageIds)
        .gte("created_at", thirtyDaysAgo);
      
      return { submissions: count || 0, pages: pageIds.length };
    },
    enabled: !!user?.id && open,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch user's profile for greeting and logo
  const { data: profile } = useQuery({
    queryKey: ["profile-name", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("company_name, email, logo_url")
        .eq("id", user.id)
        .single();
      return data;
    },
    enabled: !!user?.id && open,
  });

  // Get company initials for avatar fallback
  const getCompanyInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Generate assistant name from company name
  const getAssistantName = (companyName: string | null | undefined) => {
    if (!companyName) return "HR Assistant";
    const firstName = companyName.split(' ')[0];
    return `${firstName} HR`;
  };

  const assistantName = getAssistantName(profile?.company_name);

  // Load recent conversations on open
  useEffect(() => {
    if (open && user?.id) {
      loadRecentConversations();
      // If no current conversation, show empty state (new chat)
      if (!currentConversationId) {
        setMessages([]);
      }
    }
  }, [open, user?.id]);

  // Handle initial question when panel opens
  useEffect(() => {
    if (open && initialQuestion && initialQuestion !== processedInitialQuestion && !isLoading) {
      setProcessedInitialQuestion(initialQuestion);
      // Small delay to ensure panel is ready
      setTimeout(() => {
        sendMessage(initialQuestion);
      }, 100);
    }
  }, [open, initialQuestion, processedInitialQuestion, isLoading]);

  // Reset processed question when panel closes
  useEffect(() => {
    if (!open) {
      setProcessedInitialQuestion(null);
    }
  }, [open]);

  // Auto-scroll to bottom using last message ref
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadRecentConversations = async () => {
    if (!user?.id) return;
    
    const { data, error } = await supabase
      .from("ai_conversations")
      .select("*")
      .eq("user_id", user.id)
      .order("last_message_at", { ascending: false })
      .limit(10);

    if (!error && data) {
      setRecentConversations(data as Conversation[]);
    }
  };

  const loadConversation = async (conversationId: string) => {
    if (!user?.id) return;
    
    const { data, error } = await supabase
      .from("ai_chat_messages")
      .select("id, role, content, created_at")
      .eq("user_id", user.id)
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (!error && data) {
      setMessages(data.map(m => ({
        id: m.id,
        role: m.role as "user" | "assistant",
        content: m.content,
        createdAt: new Date(m.created_at)
      })));
      setCurrentConversationId(conversationId);
      setShowRecentChats(false);
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setCurrentConversationId(null);
    setShowRecentChats(false);
  };

  const clearHistory = async () => {
    if (!user?.id || !currentConversationId) return;
    
    const { error } = await supabase
      .from("ai_chat_messages")
      .delete()
      .eq("user_id", user.id)
      .eq("conversation_id", currentConversationId);

    if (!error) {
      setMessages([]);
      setCurrentConversationId(null);
      loadRecentConversations();
      toast.success("Conversation cleared");
    }
  };

  const copyToClipboard = async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fetchFollowUpSuggestions = async (allMessages: Message[]): Promise<string[]> => {
    try {
      const contextMessages = allMessages.slice(-4).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ messages: contextMessages, generateFollowUps: true }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.suggestions || [];
      }
    } catch (error) {
      logger.error("Failed to fetch follow-up suggestions", error);
    }
    return [];
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || !user?.id || isLoading) return;

    // Create new conversation if none exists - use stable ID
    const convId = currentConversationId || crypto.randomUUID();
    if (!currentConversationId) {
      setCurrentConversationId(convId);
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      createdAt: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      // Save user message to DB with conversation_id
      await supabase.from("ai_chat_messages").insert({
        user_id: user.id,
        role: "user",
        content: userMessage.content,
        conversation_id: convId
      });

      // Get conversation context - increased from 10 to 20 messages
      const contextMessages = [...messages.slice(-20), userMessage].map(m => ({
        role: m.role,
        content: m.content
      }));

      // Call AI chat edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ messages: contextMessages }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error("Rate limit exceeded. Please try again later.");
        }
        if (response.status === 402) {
          throw new Error("AI credits exhausted. Please add credits to continue.");
        }
        throw new Error("Failed to get AI response");
      }

      // Handle streaming response - collect full response then display
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let assistantId = crypto.randomUUID();

      // Add placeholder assistant message with "Typing..." indicator (empty content shows loading dots)
      setMessages(prev => [...prev, { id: assistantId, role: "assistant", content: "", createdAt: new Date() }]);

      let textBuffer = "";

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          const line = textBuffer.slice(0, newlineIndex).trim();
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (!line.startsWith("data: ") || line === "data: [DONE]") continue;

          try {
            const parsed = JSON.parse(line.slice(6));
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              // Don't update message content during streaming - wait for completion
            }
          } catch {
            // Incomplete JSON, continue
          }
        }
      }

      // Show complete response after streaming finishes
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId ? { ...m, content: assistantContent } : m
        )
      );

      // Save assistant message to DB with conversation_id - use the same convId from above
      if (assistantContent) {
        await supabase.from("ai_chat_messages").insert({
          user_id: user.id,
          role: "assistant",
          content: assistantContent,
          conversation_id: convId
        });
        
        // Refresh recent conversations
        loadRecentConversations();

        // Fetch follow-up suggestions after response completes
        const updatedMessages = [...messages, userMessage, { id: assistantId, role: "assistant" as const, content: assistantContent, createdAt: new Date() }];
        const suggestions = await fetchFollowUpSuggestions(updatedMessages);
        
        if (suggestions.length > 0) {
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId ? { ...m, followUpSuggestions: suggestions } : m
            )
          );
        }
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send message";
      toast.error(errorMessage);
      logger.error("AI chat error", error);
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Generate conversation title from first user message
  const conversationTitle = messages.find(m => m.role === "user")?.content.slice(0, 50) || "New Conversation";

  // Full screen view
  if (isFullScreen) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        {/* Full screen header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
          <div className="flex items-center gap-2">
            <SheetTitle className="truncate max-w-[300px]">
              {showRecentChats ? "Recent Chats" : messages.length > 0 ? conversationTitle : assistantName}
            </SheetTitle>
          </div>
          <TooltipProvider delayDuration={700} skipDelayDuration={0}>
            <div className="flex items-center gap-0.5">
              {!showRecentChats && (
                <>
                  <Tooltip defaultOpen={false}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowRecentChats(true)}
                        className="h-8 w-8 text-muted-foreground"
                        aria-label="View recent conversations"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={4}>
                      <p>View recent conversations</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip defaultOpen={false}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={startNewConversation}
                        className="h-8 w-8 text-muted-foreground"
                        aria-label="Start new conversation"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={4}>
                      <p>Start new conversation</p>
                    </TooltipContent>
                  </Tooltip>
                  {messages.length > 0 && (
                    <AIChatExportMenu 
                      messages={messages} 
                      companyName={profile?.company_name || undefined}
                      conversationTitle={conversationTitle}
                    />
                  )}
                  {messages.length > 0 && (
                    <Tooltip defaultOpen={false}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={clearHistory}
                          className="h-8 w-8 text-muted-foreground"
                          aria-label="Delete this conversation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>Delete this conversation</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
              <Tooltip defaultOpen={false}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsFullScreen(false)}
                    className="h-8 w-8 text-muted-foreground"
                    aria-label="Exit full screen"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  <p>Exit full screen</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip defaultOpen={false}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setIsFullScreen(false);
                      onOpenChange(false);
                    }}
                    className="h-8 w-8 text-muted-foreground"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  <p>Close</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
        
        {/* Data context badge */}
        {!showRecentChats && dataContext && (dataContext.submissions > 0 || dataContext.pages > 0) && (
          <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground py-2 border-b">
            <Database className="h-3 w-3" />
            <span>Analyzing {dataContext.submissions} submissions · {dataContext.pages} pages</span>
          </div>
        )}

        {/* Messages - centered content */}
        <ScrollArea className="flex-1 bg-muted/30">
          <div className="p-4 max-w-3xl mx-auto">
            {showRecentChats ? (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 h-12 mb-4"
                  onClick={startNewConversation}
                >
                  <Plus className="h-4 w-4" />
                  <span>New Chat</span>
                </Button>
                {recentConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                    <BodyText variant="secondary">No previous conversations</BodyText>
                  </div>
                ) : (
                  recentConversations.map((conv) => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => loadConversation(conv.conversation_id)}
                      className="w-full text-left p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      <p className="text-sm font-medium line-clamp-2 mb-1">
                        {conv.first_message.slice(0, 80)}{conv.first_message.length > 80 ? "..." : ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })} · {conv.message_count} messages
                      </p>
                    </button>
                  ))
                )}
              </div>
            ) : messages.length === 0 ? (
              <div className="space-y-6 max-w-md mx-auto">
                {/* Welcome section */}
                <div className="text-center py-6">
                  <Avatar className="h-12 w-12 rounded-lg bg-primary mx-auto mb-4">
                    <AvatarImage 
                      src={profile?.logo_url || undefined} 
                      className="object-cover rounded-lg" 
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground text-lg font-medium rounded-lg">
                      {getCompanyInitials(profile?.company_name || "AI")}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className="text-lg font-medium mb-1">I'm {assistantName}</h3>
                  <BodyText variant="secondary" className="max-w-[280px] mx-auto">
                    Your AI-powered HR assistant. I analyze our team's feedback and help you build a better workplace. Ask me anything.
                  </BodyText>
                </div>
                
                {/* Quick questions as pills */}
                <div className="space-y-3">
                  <BodyText className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Try asking
                  </BodyText>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_QUESTIONS.map((question) => {
                      const Icon = question.icon;
                      return (
                        <Button
                          key={question.text}
                          variant="outline"
                          size="sm"
                          className="h-9 gap-2 rounded-full hover:bg-muted/80 transition-colors"
                          onClick={() => sendMessage(question.text)}
                        >
                          <Icon className={cn("h-3.5 w-3.5", question.color)} />
                          <span className="text-sm">{question.text}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 w-full">
                {messages.map((message, index) => (
                  <div key={message.id}>
                    <div
                      className={cn(
                        "flex gap-2",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {message.role === "assistant" && (
                        <Avatar className="h-7 w-7 rounded-lg shrink-0 mt-1">
                          <AvatarImage 
                            src={profile?.logo_url || undefined} 
                            className="object-cover rounded-lg" 
                          />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium rounded-lg">
                            {getCompanyInitials(profile?.company_name || "AI")}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={cn("max-w-[85%] min-w-0", message.role === "assistant" && "group")}>
                        <div
                          className={cn(
                            "rounded-2xl px-4 py-3",
                            message.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-card border border-border shadow-sm"
                          )}
                        >
                          {message.content ? (
                            <BodyText className={cn(
                              "text-sm whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere] [word-break:break-word]",
                              message.role === "user" && "text-primary-foreground"
                            )}>
                              {stripMarkdown(message.content)}
                            </BodyText>
                          ) : (
                            <div className="flex items-center gap-2 py-1">
                              <span className="text-sm text-muted-foreground">Typing</span>
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.3s]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.15s]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" />
                            </div>
                          )}
                        </div>
                        {message.role === "assistant" && message.content && (
                          <div className="flex items-center gap-2 mt-1.5 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => copyToClipboard(message.content, message.id)}
                              aria-label="Copy message"
                            >
                              {copiedId === message.id ? (
                                <Check className="h-3 w-3 mr-1" />
                              ) : (
                                <Copy className="h-3 w-3 mr-1" />
                              )}
                              {copiedId === message.id ? "Copied" : "Copy"}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {message.role === "assistant" && 
                     message.followUpSuggestions && 
                     message.followUpSuggestions.length > 0 && 
                     index === messages.length - 1 &&
                     !isLoading && (
                      <div className="mt-3 space-y-2 ml-9">
                        <BodyText className="text-xs text-muted-foreground px-1">Suggested follow-ups</BodyText>
                        <div className="flex flex-wrap gap-2">
                          {message.followUpSuggestions.map((suggestion) => (
                            <Button
                              key={`followup-${suggestion.slice(0, 30)}`}
                              variant="outline"
                              size="sm"
                              className="h-auto min-h-[32px] text-xs rounded-full hover:bg-muted/80 py-2 px-3 max-w-full text-left whitespace-normal"
                              onClick={() => sendMessage(suggestion)}
                            >
                              <span className="line-clamp-2">{suggestion}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input - centered */}
        {!showRecentChats && (
          <div className="p-4 border-t bg-background">
            <div className="flex gap-2 max-w-3xl mx-auto">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={messages.length === 0 ? `Ask ${assistantName} anything about your team's feedback...` : "Type a follow-up question..."}
                className="min-h-[44px] max-h-32 resize-none rounded-xl"
                disabled={isLoading}
                aria-label="Message input"
              />
              <Button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                className="h-[44px] w-[44px] shrink-0 rounded-xl"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <BodyText className="text-xs text-muted-foreground mt-2 text-center">
              Press Enter to send · Shift+Enter for new line
            </BodyText>
          </div>
        )}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col p-0 bg-background">
        <SheetHeader className="p-4 border-b bg-background">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {showRecentChats && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowRecentChats(false)}
                  className="h-8 w-8"
                  aria-label="Go back"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <SheetTitle className="truncate max-w-[200px]">
                {showRecentChats ? "Recent Chats" : messages.length > 0 ? conversationTitle : assistantName}
              </SheetTitle>
            </div>
            <TooltipProvider delayDuration={700} skipDelayDuration={0}>
              <div className="flex items-center gap-0.5 mr-10">
                {!showRecentChats && (
                  <>
                    <Tooltip defaultOpen={false}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setShowRecentChats(true)}
                          className="h-8 w-8 text-muted-foreground"
                          aria-label="View recent conversations"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={4}>
                        <p>View recent conversations</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip defaultOpen={false}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={startNewConversation}
                          className="h-8 w-8 text-muted-foreground"
                          aria-label="Start new conversation"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={4}>
                        <p>Start new conversation</p>
                      </TooltipContent>
                    </Tooltip>
                    {messages.length > 0 && (
                      <AIChatExportMenu 
                        messages={messages} 
                        companyName={profile?.company_name || undefined}
                        conversationTitle={conversationTitle}
                      />
                    )}
                    {messages.length > 0 && (
                      <Tooltip defaultOpen={false}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={clearHistory}
                            className="h-8 w-8 text-muted-foreground"
                            aria-label="Delete this conversation"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p>Delete this conversation</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  <Tooltip defaultOpen={false}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsFullScreen(true)}
                        className="h-8 w-8 text-muted-foreground"
                        aria-label="Full screen"
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={4}>
                      <p>Full screen</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>
        {/* Data context badge */}
        {!showRecentChats && dataContext && (dataContext.submissions > 0 || dataContext.pages > 0) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Database className="h-3 w-3" />
              <span>Analyzing {dataContext.submissions} submissions · {dataContext.pages} pages</span>
            </div>
          )}
          <SheetDescription className="text-sm sr-only">
            Ask questions about your feedback data and get AI-powered insights
          </SheetDescription>
        </SheetHeader>

        {/* Messages */}
        <ScrollArea className="flex-1 bg-muted/30">
          <div className="p-4 max-w-full overflow-hidden">
            {showRecentChats ? (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 h-12 mb-4"
                  onClick={startNewConversation}
                >
                  <Plus className="h-4 w-4" />
                  <span>New Chat</span>
                </Button>
                {recentConversations.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                    <BodyText variant="secondary">No previous conversations</BodyText>
                  </div>
                ) : (
                  recentConversations.map((conv) => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => loadConversation(conv.conversation_id)}
                      className="w-full text-left p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                    >
                      <p className="text-sm font-medium line-clamp-2 mb-1">
                        {conv.first_message.slice(0, 80)}{conv.first_message.length > 80 ? "..." : ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })} · {conv.message_count} messages
                      </p>
                    </button>
                  ))
                )}
              </div>
            ) : messages.length === 0 ? (
              <div className="space-y-6">
                {/* Welcome section */}
                <div className="text-center py-6">
                  <Avatar className="h-12 w-12 rounded-lg bg-primary mx-auto mb-4">
                    <AvatarImage 
                      src={profile?.logo_url || undefined} 
                      className="object-cover rounded-lg" 
                    />
                    <AvatarFallback className="bg-primary text-primary-foreground text-lg font-medium rounded-lg">
                      {getCompanyInitials(profile?.company_name || "AI")}
                    </AvatarFallback>
                  </Avatar>
                  <h3 className="text-lg font-medium mb-1">I'm {assistantName}</h3>
                  <BodyText variant="secondary" className="max-w-[280px] mx-auto">
                    Your AI-powered HR assistant. I analyze our team's feedback and help you build a better workplace. Ask me anything.
                  </BodyText>
                </div>
                
                {/* Quick questions as pills */}
                <div className="space-y-3">
                  <BodyText className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Try asking
                  </BodyText>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_QUESTIONS.map((question) => {
                      const Icon = question.icon;
                      return (
                        <Button
                          key={question.text}
                          variant="outline"
                          size="sm"
                          className="h-9 gap-2 rounded-full hover:bg-muted/80 transition-colors"
                          onClick={() => sendMessage(question.text)}
                        >
                          <Icon className={cn("h-3.5 w-3.5", question.color)} />
                          <span className="text-sm">{question.text}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 w-full">
                {messages.map((message, index) => (
                  <div key={message.id}>
                    <div
                      className={cn(
                        "flex gap-2",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      {/* Assistant avatar */}
                      {message.role === "assistant" && (
                        <Avatar className="h-7 w-7 rounded-lg shrink-0 mt-1">
                          <AvatarImage 
                            src={profile?.logo_url || undefined} 
                            className="object-cover rounded-lg" 
                          />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium rounded-lg">
                            {getCompanyInitials(profile?.company_name || "AI")}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={cn("max-w-[85%] min-w-0", message.role === "assistant" && "group")}>
                        <div
                          className={cn(
                            "rounded-2xl px-4 py-3",
                            message.role === "user"
                              ? "bg-primary text-primary-foreground"
                              : "bg-card border border-border shadow-sm",
                            message.role === "assistant" && "max-h-[400px] overflow-y-auto"
                          )}
                        >
                          {message.content ? (
                            <BodyText className={cn(
                              "text-sm whitespace-pre-wrap leading-relaxed break-words [overflow-wrap:anywhere] [word-break:break-word]",
                              message.role === "user" && "text-primary-foreground"
                            )}>
                              {stripMarkdown(message.content)}
                            </BodyText>
                          ) : (
                            <div className="flex items-center gap-2 py-1">
                              <span className="text-sm text-muted-foreground">Typing</span>
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.3s]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce [animation-delay:-0.15s]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" />
                            </div>
                          )}
                        </div>
                        {/* Copy button and timestamp for assistant messages */}
                        {message.role === "assistant" && message.content && (
                          <div className="flex items-center gap-2 mt-1.5 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                              onClick={() => copyToClipboard(message.content, message.id)}
                              aria-label="Copy message"
                            >
                              {copiedId === message.id ? (
                                <Check className="h-3 w-3 mr-1" />
                              ) : (
                                <Copy className="h-3 w-3 mr-1" />
                              )}
                              {copiedId === message.id ? "Copied" : "Copy"}
                            </Button>
                            {message.createdAt && (
                              <span className="text-xs text-muted-foreground">
                                {formatDistanceToNow(message.createdAt, { addSuffix: true })}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Follow-up suggestions - show only for the last assistant message */}
                    {message.role === "assistant" && 
                     message.followUpSuggestions && 
                     message.followUpSuggestions.length > 0 && 
                     index === messages.length - 1 &&
                     !isLoading && (
                      <div className="mt-3 space-y-2 ml-9">
                        <BodyText className="text-xs text-muted-foreground px-1">Suggested follow-ups</BodyText>
                        <div className="flex flex-wrap gap-2">
                          {message.followUpSuggestions.map((suggestion) => (
                            <Button
                              key={`followup-${suggestion.slice(0, 30)}`}
                              variant="outline"
                              size="sm"
                              className="h-auto min-h-[32px] text-xs rounded-full hover:bg-muted/80 py-2 px-3 max-w-full text-left whitespace-normal"
                              onClick={() => sendMessage(suggestion)}
                            >
                              <span className="line-clamp-2">{suggestion}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input - hidden when viewing recent chats */}
        {!showRecentChats && (
          <div className="p-4 border-t bg-background">
            <div className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={messages.length === 0 ? `Ask ${assistantName} anything about your team's feedback...` : "Type a follow-up question..."}
                className="min-h-[44px] max-h-32 resize-none rounded-xl"
                disabled={isLoading}
                aria-label="Message input"
              />
              <Button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                className="h-[44px] w-[44px] shrink-0 rounded-xl"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <BodyText className="text-xs text-muted-foreground mt-2 text-center">
              Press Enter to send · Shift+Enter for new line
            </BodyText>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
